import React from 'react';
import { motion } from 'framer-motion';

interface ServiceCardProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  delay?: number;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ServiceCard: React.FC<ServiceCardProps> = ({ title, description, icon, delay = 0 }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay }}
      className="bg-white p-8 rounded-xl shadow-lg hover:shadow-2xl transition-all duration-500 group border-b-4 border-secondary/70 hover:border-secondary"
      whileHover={{
        y: -5,
        transition: { duration: 0.2 }
      }}
    >
      <div className="w-16 h-16 bg-secondary/10 rounded-full flex items-center justify-center mb-6 text-secondary group-hover:bg-secondary group-hover:text-white transition-all duration-300">
        {icon}
      </div>
      <h3 className="text-2xl font-bold mb-4 group-hover:text-secondary transition-colors duration-300">{title}</h3>
      <p className="text-gray-600 leading-relaxed">{description}</p>
    </motion.div>
  );
};

interface ImageServiceCardProps {
  title: string;
  description: string;
  imageSrc: string;
  delay?: number;
  icon?: React.ReactNode;
  imageObjectPosition?: string;
  href?: string;
}

const ImageServiceCard: React.FC<ImageServiceCardProps> = ({ title, description, imageSrc, icon, delay = 0, imageObjectPosition = 'center 20%', href }) => {
  const Component = href ? 'a' : 'div';
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay }}
      className={`bg-black overflow-hidden rounded-xl shadow-lg hover:shadow-2xl transition-all duration-500 group relative h-96${href ? ' cursor-pointer' : ''}`}
      whileHover={{
        y: -5,
        transition: { duration: 0.2 }
      }}
      {...(href ? { onClick: () => { window.location.href = href; } } : {})}
    >
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-transparent z-10"></div>
      <img
        src={`${process.env.PUBLIC_URL}/${imageSrc}`}
        alt={title}
        loading="lazy"
        decoding="async"
        className="w-full h-full object-cover object-center absolute inset-0 brightness-110"
        style={{ objectPosition: imageObjectPosition }}
      />
      <div className="relative z-20 p-8 h-full flex flex-col justify-end">
        {icon && (
          <div className="w-16 h-16 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center mb-6 text-white group-hover:bg-secondary group-hover:text-white transition-all duration-300">
            {icon}
          </div>
        )}
        <h3 className="text-2xl font-bold mb-4 text-primary uppercase" style={{ fontFamily: "'Poppins', sans-serif" }}>{title}</h3>
        <p className="text-white/95 leading-relaxed min-h-[4.5rem]">{description}</p>
      </div>
    </motion.div>
  );
};

const Services: React.FC = () => {
  return (
    <section id="services" className="relative overflow-hidden pt-8 pb-24 md:pt-24">
      <div className="container relative z-10">
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <h2 className="text-3xl md:text-5xl font-bold mb-5 tracking-wide uppercase" style={{ fontFamily: "'Poppins', sans-serif", color: '#5E495C' }}>Services</h2>
            <p className="text-gray-600 max-w-3xl mx-auto text-lg font-light uppercase" style={{ fontFamily: "'Poppins', sans-serif" }}>
              Customized services I offer to future princesses
            </p>
          </motion.div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-10">
          <ImageServiceCard
            title="1:1 COACHING"
            description="For princesses who want support, guidance and help creating sustainable training plans tailored to specific goals."
            imageSrc="images/online-coaching.webp"
            imageObjectPosition="center 40%"
            delay={0.1}
            href="#contact"
          />

          <ImageServiceCard
            title="Free Resources"
            description="Free guides and tools to kickstart your fitness journey — nutrition tips, volume eating, and more."
            imageSrc="images/nutrition.webp"
            delay={0.2}
            href="/free-resources"
          />

          <ImageServiceCard
            title="Buy Program"
            description="Pre-made training program as a more affordable alternative to coaching. One-time payment, no commitment or subscription."
            imageSrc="images/buy-program.webp"
            delay={0.3}
            href="/programs"
          />
        </div>
        
      </div>
    </section>
  );
};

export default Services; 